import { useCallback, useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  IconAlertTriangle,
  IconLoader2,
  IconRefresh,
  IconUpload,
} from "@tabler/icons-react";
import axios from "axios";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import {
  MAX_RETAILER_WORKBOOK_BYTES,
  validateRetailerWorkbook,
} from "../features/voucher-matching/retailer-store-upload";
import { formatDateTime } from "../utils/dateFormat";
import api from "../lib/api";
import MainCard from "./MainCard";
import { WriteAccessGuard } from "./WriteAccessGuard";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const uploadSchema = z.object({
  file: z.custom<File>((value) => value instanceof File, {
    message: "Select a retailer details workbook",
  }).superRefine((file, context) => {
    const message = validateRetailerWorkbook(file);
    if (message) context.addIssue({ code: "custom", message });
  }),
});

type UploadForm = z.infer<typeof uploadSchema>;

interface RetailerStoreStatus {
  filename: string | null;
  acceptedCount: number;
  skippedCount: number;
  uploadedAt: string | null;
}

interface RetailerStoreUploadResult extends RetailerStoreStatus {
  success: true;
  message: string;
  filename: string;
  uploadedAt: string;
  skippedRowNumbers: number[];
}

interface RetailerStoreStatusResponse {
  success: true;
  data: RetailerStoreStatus;
}

interface RetailerStoreUploadResponse {
  success: true;
  data: RetailerStoreUploadResult;
}

function getErrorMessage(error: unknown, fallback: string): string {
  if (!axios.isAxiosError(error)) return fallback;
  const payload = error.response?.data as
    | { message?: unknown; error?: { message?: unknown } }
    | undefined;
  if (typeof payload?.error?.message === "string") {
    return payload.error.message;
  }
  return typeof payload?.message === "string" ? payload.message : fallback;
}

export function RetailerStoreUploadCard() {
  const [status, setStatus] = useState<RetailerStoreStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [statusError, setStatusError] = useState("");
  const [uploadError, setUploadError] = useState("");
  const [uploadSuccess, setUploadSuccess] = useState("");
  const [inputKey, setInputKey] = useState(0);
  const form = useForm<UploadForm>({
    resolver: zodResolver(uploadSchema),
  });

  const loadStatus = useCallback(async () => {
    setStatusLoading(true);
    setStatusError("");
    try {
      const response = await api.get<RetailerStoreStatusResponse>(
        "/spar-mobile/retailer-stores/last-updated",
      );
      setStatus(response.data.data);
    } catch (error: unknown) {
      setStatusError(
        getErrorMessage(error, "Failed to load the retailer store snapshot"),
      );
    } finally {
      setStatusLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  const submitUpload = form.handleSubmit(async ({ file }) => {
    setUploadError("");
    setUploadSuccess("");
    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await api.post<RetailerStoreUploadResponse>(
        "/spar-mobile/retailer-stores/upload",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      const result = response.data.data;
      setStatus(result);
      setUploadSuccess(
        `${result.acceptedCount.toLocaleString()} live stores loaded from ${result.filename}.` +
          (result.skippedCount > 0
            ? ` ${result.skippedCount} incomplete row${result.skippedCount === 1 ? " was" : "s were"} skipped.`
            : ""),
      );
      form.reset();
      setInputKey((value) => value + 1);
    } catch (error: unknown) {
      setUploadError(
        getErrorMessage(error, "Failed to upload the retailer details workbook"),
      );
    }
  });

  const selectedFile = form.watch("file");

  return (
    <MainCard className="mb-6">
      <div className="space-y-4">
        <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h3 className="text-base font-bold">Retailer store master</h3>
            <p className="text-sm text-muted-foreground">
              Controls which stores are classified as activated in commission
              reconciliation reports.
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void loadStatus()}
            disabled={statusLoading}
          >
            {statusLoading ? (
              <IconLoader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <IconRefresh className="mr-2 h-4 w-4" />
            )}
            Refresh status
          </Button>
        </div>

        <Alert variant="warning">
          <IconAlertTriangle />
          <AlertTitle>Full replacement</AlertTitle>
          <AlertDescription>
            Uploading a workbook replaces the complete live-store list. Only
            rows on the RETAILERS sheet with both Store Id and Store Name are
            loaded.
          </AlertDescription>
        </Alert>

        {statusError && (
          <Alert variant="destructive">
            <AlertDescription>{statusError}</AlertDescription>
          </Alert>
        )}

        {!statusError && (
          <div className="grid gap-3 rounded-lg border border-border/60 bg-muted/20 p-4 text-sm sm:grid-cols-3">
            <div>
              <span className="text-muted-foreground">Current workbook</span>
              <p className="mt-1 font-medium break-all">
                {statusLoading ? "Loading..." : status?.filename || "Not uploaded"}
              </p>
            </div>
            <div>
              <span className="text-muted-foreground">Live stores</span>
              <p className="mt-1 font-medium">
                {statusLoading
                  ? "Loading..."
                  : (status?.acceptedCount ?? 0).toLocaleString()}
              </p>
            </div>
            <div>
              <span className="text-muted-foreground">Last uploaded</span>
              <p className="mt-1 font-medium">
                {statusLoading ? "Loading..." : formatDateTime(status?.uploadedAt)}
              </p>
            </div>
          </div>
        )}

        <WriteAccessGuard page="spar-mobile">
          <form onSubmit={(event) => void submitUpload(event)} className="space-y-3">
            <div className="grid gap-2">
              <Label htmlFor="retailer-store-workbook">
                Retailer details workbook
              </Label>
              <Controller
                name="file"
                control={form.control}
                render={({ field, fieldState }) => (
                  <>
                    <Input
                      key={inputKey}
                      id="retailer-store-workbook"
                      ref={field.ref}
                      name={field.name}
                      type="file"
                      accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                      aria-invalid={fieldState.invalid}
                      onBlur={field.onBlur}
                      onChange={(event) =>
                        field.onChange(event.target.files?.[0])
                      }
                    />
                    {fieldState.error && (
                      <p className="text-sm text-destructive" role="alert">
                        {fieldState.error.message}
                      </p>
                    )}
                  </>
                )}
              />
              <p className="text-xs text-muted-foreground">
                .xlsx only, up to {MAX_RETAILER_WORKBOOK_BYTES / 1024 / 1024} MiB
              </p>
            </div>

            <Button
              type="submit"
              disabled={!selectedFile || form.formState.isSubmitting}
            >
              {form.formState.isSubmitting ? (
                <IconLoader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <IconUpload className="mr-2 h-4 w-4" />
              )}
              {form.formState.isSubmitting ? "Uploading..." : "Replace live stores"}
            </Button>
          </form>
        </WriteAccessGuard>

        {uploadError && (
          <Alert variant="destructive">
            <AlertDescription>{uploadError}</AlertDescription>
          </Alert>
        )}
        {uploadSuccess && (
          <Alert variant="success">
            <AlertDescription>{uploadSuccess}</AlertDescription>
          </Alert>
        )}
      </div>
    </MainCard>
  );
}
